import { navBarOpenAtom } from '@australiangreens/ag-internal-components';
import { Accordion, AccordionDetails, AccordionSummary, Typography } from '@mui/material';
import { useAtomValue, useSetAtom } from 'jotai';
import { Dispatch, SetStateAction, useContext, useEffect } from 'react';

import { NotRenewingContext } from '@/App';
import { SearchTreeNode } from '@/types';
import { hasNotRenewing } from '@/util';
import { debugAtom } from '@pages/AdvancedSearchListCreatorPage';
import SearchTreeBuilder, {
  SearchTreeBuilderState,
  SearchTreeChangeHandler,
} from '@pages/AdvancedSearchListCreatorPage/SearchTreeBuilder';
import { ConfirmDeleteRuleOrGroupProvider } from '@pages/SearchTemplatePage/deletionConfirmation/ConfirmDeleteRuleOrGroupContext';
import { CurrentSearchTreeProvider } from './deletionConfirmation/CurrentSearchTreeContext';

type SearchTreeBuilderSectionProps = {
  searchTreeBuilderState: SearchTreeBuilderState;
  setSearchTreeBuilderState: Dispatch<SetStateAction<SearchTreeBuilderState>>;
  searchTree: SearchTreeNode | undefined;
  onSearchTreeChange: SearchTreeChangeHandler;
  templateNameToDisplay: string | undefined;
  /** Leaf node IDs that have hidden placeholders (primary field empty); show red border in tree. */
  leafNodeIdsWithHiddenPlaceholders: Set<string>;
  /** Leaf node ID of placeholder entry selected by click; show pink background on corresponding filter. */
  selectedPlaceholderLeafNodeId: string | null;
  /** Passed to SearchTreeBuilder root for tests / E2E (see `data-testid` on SearchTreeBuilder). */
  searchTreeBuilderDataTestId?: string;
};

const HIDDEN_PLACEHOLDER_WARNING_CLASS = 'hidden-placeholder-warning';
const PLACEHOLDER_HOVER_HIGHLIGHT_CLASS = 'placeholder-hover-highlight';

export default function SearchTreeBuilderSection({
  templateNameToDisplay,
  searchTreeBuilderState,
  setSearchTreeBuilderState,
  searchTree,
  onSearchTreeChange,
  leafNodeIdsWithHiddenPlaceholders,
  selectedPlaceholderLeafNodeId,
  searchTreeBuilderDataTestId,
}: SearchTreeBuilderSectionProps) {
  const debug = useAtomValue(debugAtom);
  const setIsNavbarOpen = useSetAtom(navBarOpenAtom);
  const { setNotRenewingPresent } = useContext(NotRenewingContext);

  // Apply red border to RAQB rule containers for leaf nodes with hidden placeholders.
  // Targets DOM by data-id (must match _leafNodeId from tree; set in treeMapping from RAQB rule.id).
  // If SearchTreeBuilder or RAQB DOM structure changes, this selector may need updating.
  useEffect(() => {
    document
      .querySelectorAll(`.${HIDDEN_PLACEHOLDER_WARNING_CLASS}`)
      .forEach((el) => el.classList.remove(HIDDEN_PLACEHOLDER_WARNING_CLASS));
    for (const leafNodeId of leafNodeIdsWithHiddenPlaceholders) {
      const el = document.querySelector(`[data-id="${leafNodeId}"]`);
      el?.classList.add(HIDDEN_PLACEHOLDER_WARNING_CLASS);
    }
    return () => {
      document
        .querySelectorAll(`.${HIDDEN_PLACEHOLDER_WARNING_CLASS}`)
        .forEach((el) => el.classList.remove(HIDDEN_PLACEHOLDER_WARNING_CLASS));
    };
  }, [leafNodeIdsWithHiddenPlaceholders]);

  useEffect(() => {
    document
      .querySelectorAll(`.${PLACEHOLDER_HOVER_HIGHLIGHT_CLASS}`)
      .forEach((el) => el.classList.remove(PLACEHOLDER_HOVER_HIGHLIGHT_CLASS));
    if (selectedPlaceholderLeafNodeId) {
      const ruleEl = document.querySelector(`[data-id="${selectedPlaceholderLeafNodeId}"]`);
      ruleEl?.classList.add(PLACEHOLDER_HOVER_HIGHLIGHT_CLASS);
    }
    return () => {
      document
        .querySelectorAll(`.${PLACEHOLDER_HOVER_HIGHLIGHT_CLASS}`)
        .forEach((el) => el.classList.remove(PLACEHOLDER_HOVER_HIGHLIGHT_CLASS));
    };
  }, [selectedPlaceholderLeafNodeId]);

  // Called when the search tree (aka template tree in this component) state is
  // updated by the SearchTreeBuilder, as opposed to the user updating
  // individual placeholder descriptions etc, which is handled by PlaceholdersPanelContent
  const handleSearchTreeChange: SearchTreeChangeHandler = (
    searchTree,
    isIncomplete,
    actionMeta
  ) => {
    onSearchTreeChange(searchTree, isIncomplete, actionMeta);

    const isNotRenewing = hasNotRenewing(searchTree);
    setNotRenewingPresent(isNotRenewing);
  };

  useEffect(() => {
    setIsNavbarOpen(false);
  }, [setIsNavbarOpen]);

  return (
    <>
      {' '}
      <Typography component="h2" variant="h5">
        {templateNameToDisplay ? templateNameToDisplay : 'New Template'}
      </Typography>
      <div className="templateBuilder" style={{ marginTop: '24px' }}>
        <Accordion expanded square={true} sx={{ borderRadius: '4px' }}>
          <AccordionSummary>
            <Typography component="h3" variant="h6">
              Criteria
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <CurrentSearchTreeProvider currentSearchTree={searchTree}>
              <ConfirmDeleteRuleOrGroupProvider>
                <SearchTreeBuilder
                  onSearchTreeChange={handleSearchTreeChange}
                  debug={debug}
                  searchTreeBuilderState={searchTreeBuilderState}
                  setSearchTreeBuilderState={setSearchTreeBuilderState}
                  data-testid={searchTreeBuilderDataTestId}
                />
              </ConfirmDeleteRuleOrGroupProvider>
            </CurrentSearchTreeProvider>
          </AccordionDetails>
        </Accordion>
      </div>
      {debug && (
        <Accordion>
          <AccordionSummary>
            <Typography component="span">
              Search tree (placeholders are not updated dynamically)
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ fontSize: '10px' }}>
            <pre>{searchTree ? JSON.stringify(searchTree, null, 4) : 'Undefined'}</pre>
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
}
